import { useEffect, useState, useCallback } from 'react'
import { TelegramWebApp, TelegramUser } from '../types'
import { showToast } from '../components/ui/Toast'

export function useTelegramBotAPI() {
  const [user, setUser] = useState<TelegramUser | null>(null)
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [initData, setInitData] = useState<string>('')
  const [startParam, setStartParam] = useState<string>('')
  const [ready, setReady] = useState(false)
  const [isEnvReady, setIsEnvReady] = useState(false)

  const resolveStartParam = (tg: TelegramWebApp | undefined): string => {
    // Keep original case: fantasy share tokens are hex and dream_/fantasy_
    // prefixes must stay parseable. Referral codes are uppercased at register.
    // 1) مقدار استاندارد تلگرام در initDataUnsafe
    const unsafe = (tg as any)?.initDataUnsafe?.start_param as string | undefined
    if (unsafe) return unsafe.trim()
    // 2) داخل رشته initData (کوئری)
    if (tg?.initData) {
      try {
        const v = new URLSearchParams(tg.initData).get('start_param')
        if (v) return v.trim()
      } catch {}
    }
    // 3) fallback از URL مرورگر (برای Mini App با ?startapp=CODE )
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const fromUrl = urlParams.get('startapp') || urlParams.get('start_param') || urlParams.get('tgWebAppStartParam')
      if (fromUrl) return fromUrl.trim()
    } catch {}
    return ''
  }

  useEffect(() => {
    const checkTelegram = () => {
      const tg = window.Telegram?.WebApp
      if (tg) {
        setIsEnvReady(true)
        setWebApp(tg)
        setInitData(tg.initData || '')
        setStartParam(resolveStartParam(tg))

        const telegramUser = tg.initDataUnsafe?.user ?? null
        if (telegramUser) {
          setUser({
            id: telegramUser.id,
            first_name: telegramUser.first_name,
            last_name: telegramUser.last_name,
            username: telegramUser.username,
            photo_url: telegramUser.photo_url,
            language_code: telegramUser.language_code,
          })
        }

        if (tg.themeParams?.bg_color) {
          document.body.style.backgroundColor = tg.themeParams.bg_color
        }
        if (tg.themeParams?.text_color) {
          document.body.style.color = tg.themeParams.text_color
        }

        tg.ready()
        tg.expand()
        setReady(true)
      } else {
        setIsEnvReady(true)
        setReady(true)
        // حتی بدون Telegram WebApp، startapp را از URL بخوان (برای تست مرورگر)
        setStartParam(resolveStartParam(undefined))
      }
    }

    checkTelegram()

    const interval = setInterval(() => {
      if (!webApp && window.Telegram?.WebApp) {
        checkTelegram()
      }
    }, 500)

    return () => clearInterval(interval)
  }, [webApp])

  const showAlert = useCallback((msg: string) => {
    showToast(msg, 'info')
  }, [])

  const showConfirm = useCallback(async (msg: string): Promise<boolean> => {
    if (webApp) {
      return webApp.showConfirm(msg)
    }
    return confirm(msg)
  }, [webApp])

  const close = useCallback(() => {
    if (webApp) {
      webApp.close()
    }
  }, [webApp])

  return {
    user,
    webApp,
    initData,
    startParam,
    ready,
    isEnvReady,
    showAlert,
    showConfirm,
    close,
  }
}